import { useState, useEffect } from 'react';

export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ScreenSizeHookReturn {
  screenSize: ScreenSize;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

// Default Tailwind CSS breakpoints
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

/**
 * Custom hook to detect screen size and device type
 * @param customBreakpoints - Optional custom breakpoints object
 * @returns ScreenSizeHookReturn - Object containing screen size info
 */
export const useScreenSize = (customBreakpoints?: Partial<typeof BREAKPOINTS>): ScreenSizeHookReturn => {
  const breakpoints = { ...BREAKPOINTS, ...customBreakpoints };

  const getScreenSize = (width: number): ScreenSize => {
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  };

  const [screenInfo, setScreenInfo] = useState<ScreenSizeHookReturn>(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return {
        screenSize: 'md',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        width: 1024,
      };
    }

    const width = window.innerWidth;
    const screenSize = getScreenSize(width);

    return {
      screenSize,
      isMobile: screenSize === 'xs' || screenSize === 'sm',
      isTablet: screenSize === 'md',
      isDesktop: screenSize === 'lg' || screenSize === 'xl',
      width,
    };
  });

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      const screenSize = getScreenSize(width);

      setScreenInfo({
        screenSize,
        isMobile: screenSize === 'xs' || screenSize === 'sm',
        isTablet: screenSize === 'md',
        isDesktop: screenSize === 'lg' || screenSize === 'xl',
        width,
      });
    };

    // Set initial value
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoints.sm, breakpoints.md, breakpoints.lg, breakpoints.xl]);

  return screenInfo;
};

/**
 * Legacy hook for backward compatibility
 * @param breakpoint - The breakpoint in pixels to consider as mobile (default: 768)
 * @returns boolean - true if the device is mobile, false otherwise
 */
export const useMobile = (breakpoint: number = 639): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Set initial value
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint]);

  return isMobile;
};

export default useScreenSize;
